import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import 'moment/locale/fr-ca';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import {
  BellNotificationChange,
  BellNotificationFilter,
  BellNotificationModel,
} from '../../../api/models/NotificationCenterModel';
import { NotificationCenterService } from '../../../api/notification-center.service';
import { GT2PageAbstract } from '../../../content/abstract/GT2PageAbstract';
import { BellNotificationsService } from '../../../services/bell-notifications.service';
import { BellPanelService } from '../../../services/bell-panel.service';
import { AppRoutes } from '../../../const/AppRoutes';
import { locale as english } from '../i18n/en-CA';
import { locale as french } from '../i18n/fr-CA';
import { NotificationCenterTools } from '../notification-center-tools';

type BellPanelError = 'generic' | null;

/** One line of the list: the notification + what is computed once for the view. */
interface BellRow {
  notification: BellNotificationModel;
  icon: string;
  relativeDate: string;
  fullDate: string;
  canOpen: boolean;
}

/**
 * Right side panel « Notifications » (cloche de la barre du haut) —
 * registered in layout-1 (`bellPanel`). Offered to every plan and every role.
 *
 * Filters Actives / Non lues / Archivées, newest first, « Afficher plus »
 * pagination. A notification received in real time is inserted on top while
 * the panel is open (BellNotificationsService.created$).
 */
@Component({
  selector: 'app-bell-panel',
  templateUrl: './bell-panel.component.html',
  styleUrls: ['./bell-panel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
})
export class BellPanelComponent
  extends GT2PageAbstract
  implements OnInit, OnDestroy
{
  private static readonly PAGE_SIZE = 20;
  /** Highest `limit` the API accepts. */
  private static readonly MAX_LIMIT = 100;
  private static readonly RELATIVE_DATE_REFRESH_MS = 60 * 1000;

  readonly filters: { key: BellNotificationFilter; labelKey: string }[] = [
    { key: 'ACTIVE', labelKey: 'NOTIFICATION_CENTER.FILTER_ACTIVE' },
    { key: 'NEW', labelKey: 'NOTIFICATION_CENTER.FILTER_NEW' },
    { key: 'ARCHIVED', labelKey: 'NOTIFICATION_CENTER.FILTER_ARCHIVED' },
  ];

  filter: BellNotificationFilter = 'ACTIVE';
  rows: BellRow[] = [];
  total = 0;
  unreadCount = 0;
  override loading = false;
  loadingMore = false;
  markingAllRead = false;
  errorState: BellPanelError = null;
  /** uuid of the notification whose action is in flight. */
  busyUuid: string | null = null;

  private listSub?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private bellService: BellNotificationsService,
    private panelService: BellPanelService,
    private notificationCenterService: NotificationCenterService,
    private translationLoader: FuseTranslationLoaderService,
    private toastr: ToastrService,
    private logger: NGXLogger,
    public translate: TranslateService,
    public dialog: MatDialog,
    public router: Router,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
  }

  ngOnInit(): void {
    this.reload();

    this.bellService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => (this.unreadCount = count));

    this.bellService.created$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => this.insertOnTop(notification));

    this.bellService.updated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => this.applyUpdate(notification));

    this.bellService.resync$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reload(true));

    timer(
      BellPanelComponent.RELATIVE_DATE_REFRESH_MS,
      BellPanelComponent.RELATIVE_DATE_REFRESH_MS,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshRelativeDates());
  }

  ngOnDestroy(): void {
    this.listSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Getters of the template
  // ---------------------------------------------------------------------------

  get hasMore(): boolean {
    return this.rows.length < this.total;
  }

  get isEmpty(): boolean {
    return !this.loading && !this.errorState && this.rows.length === 0;
  }

  get emptyTitleKey(): string {
    return 'NOTIFICATION_CENTER.EMPTY_' + this.filter + '_TITLE';
  }

  get emptyTextKey(): string {
    return 'NOTIFICATION_CENTER.EMPTY_' + this.filter;
  }

  public trackByUuid(index: number, row: BellRow): string {
    return row.notification.uuid;
  }

  // ---------------------------------------------------------------------------
  // Panel
  // ---------------------------------------------------------------------------

  public close(): void {
    this.panelService.emitResult(false);
  }

  public onPreferences(): void {
    this.close();
    const prefix = (localStorage.getItem('PREFIX') || '') + '/';
    this.router.navigateByUrl(
      prefix + AppRoutes.PREFERENCES + '/mes-notifications',
    );
  }

  public onFilterChange(filter: BellNotificationFilter): void {
    if (!filter || filter === this.filter) {
      return;
    }
    this.filter = filter;
    this.reload();
  }

  // ---------------------------------------------------------------------------
  // List
  // ---------------------------------------------------------------------------

  /**
   * (Re)loads the list from its first line. `quiet` keeps the lines on
   * screen while loading and reads as many lines as already shown (list
   * possibly stale: socket reconnected, change made in another tab).
   */
  public reload(quiet: boolean = false): void {
    const limit = quiet
      ? Math.min(
          BellPanelComponent.MAX_LIMIT,
          Math.max(BellPanelComponent.PAGE_SIZE, this.rows.length),
        )
      : BellPanelComponent.PAGE_SIZE;

    this.listSub?.unsubscribe();
    this.errorState = quiet ? this.errorState : null;
    this.loading = !quiet;
    this.loadingMore = false;
    if (!quiet) {
      this.rows = [];
      this.total = 0;
    }

    const filter = this.filter;
    this.listSub = this.notificationCenterService
      .getBellNotifications(filter, 1, limit)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.errorState = null;
          this.rows = (response?.data ?? []).map((item) => this.toRow(item));
          this.total = response?.meta?.pagination?.total ?? this.rows.length;
          this.bellService.setUnreadCount(response?.meta?.unread_count);
        },
        error: (error) => {
          this.logger.error(
            'BellPanelComponent.reload() -> ERROR: ' + JSON.stringify(error),
          );
          this.loading = false;
          if (!quiet) {
            this.errorState = 'generic';
          }
        },
      });
  }

  public onShowMore(): void {
    if (this.loadingMore || this.loading || !this.hasMore) {
      return;
    }
    this.loadingMore = true;
    // The page is derived from the lines on screen: real-time inserts and
    // lines removed by an action shift the pages of the server. Lines
    // received twice are skipped below.
    const nextPage =
      Math.floor(this.rows.length / BellPanelComponent.PAGE_SIZE) + 1;

    this.listSub?.unsubscribe();
    this.listSub = this.notificationCenterService
      .getBellNotifications(this.filter, nextPage, BellPanelComponent.PAGE_SIZE)
      .subscribe({
        next: (response) => {
          this.loadingMore = false;
          this.total = response?.meta?.pagination?.total ?? this.total;

          const known = new Set(this.rows.map((row) => row.notification.uuid));
          const added = (response?.data ?? [])
            .filter((item) => !known.has(item.uuid))
            .map((item) => this.toRow(item));
          this.rows = this.rows.concat(added);
          this.bellService.setUnreadCount(response?.meta?.unread_count);
          if (added.length === 0 && this.rows.length < this.total) {
            // Nothing new although lines remain: the list moved too much,
            // read it again from the top.
            this.reload(true);
          }
        },
        error: (error) => {
          this.logger.error(
            'BellPanelComponent.onShowMore() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.loadingMore = false;
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' BEL001',
            this.router,
          );
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /** Follows action_type, marks read, closes the panel when a record opens. */
  public onOpen(row: BellRow): void {
    const opened = this.bellService.open(row.notification);
    if (opened && row.notification.action_type === 'RECORD') {
      this.close();
    }
  }

  public onChange(row: BellRow, change: BellNotificationChange): void {
    if (this.busyUuid) {
      return;
    }
    this.busyUuid = row.notification.uuid;

    this.bellService
      .change(row.notification, change)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // The line is updated or removed by applyUpdate() (updated$).
          this.busyUuid = null;
        },
        error: (error) => {
          this.logger.error(
            'BellPanelComponent.onChange() -> ERROR: ' + JSON.stringify(error),
          );
          this.busyUuid = null;
          if (error?.status === 404) {
            this.toastr.warning(
              this.translate.instant('NOTIFICATION_CENTER.NOT_FOUND'),
            );
            this.reload(true);
            return;
          }
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('NOTIFICATION_CENTER.ACTION_ERROR') +
              ' BEL002',
            this.router,
          );
        },
      });
  }

  public onMarkAllRead(): void {
    if (this.markingAllRead) {
      return;
    }
    this.markingAllRead = true;

    this.bellService
      .readAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.markingAllRead = false;
          this.toastr.success(
            this.translate.instant('NOTIFICATION_CENTER.MARK_ALL_READ_DONE'),
          );
          this.reload(this.filter !== 'NEW');
        },
        error: (error) => {
          this.logger.error(
            'BellPanelComponent.onMarkAllRead() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.markingAllRead = false;
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('NOTIFICATION_CENTER.ACTION_ERROR') +
              ' BEL003',
            this.router,
          );
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  public severityClass(notification: BellNotificationModel): string {
    return (
      'bell-severity--' +
      NotificationCenterTools.severity(notification.severity).toLowerCase()
    );
  }

  public severityLabelKey(notification: BellNotificationModel): string {
    return (
      'NOTIFICATION_CENTER.SEVERITY.' +
      NotificationCenterTools.severity(notification.severity)
    );
  }

  /** Does the notification belong to the list on screen? */
  private matchesFilter(notification: BellNotificationModel): boolean {
    switch (this.filter) {
      case 'NEW':
        return !notification.is_read && !notification.is_archived;
      case 'READ':
        return notification.is_read && !notification.is_archived;
      case 'ARCHIVED':
        return notification.is_archived;
      case 'ACTIVE':
      default:
        return !notification.is_archived;
    }
  }

  private insertOnTop(notification: BellNotificationModel): void {
    if (!notification?.uuid || !this.matchesFilter(notification)) {
      return;
    }
    if (this.rows.some((row) => row.notification.uuid === notification.uuid)) {
      return;
    }
    this.rows = [this.toRow(notification)].concat(this.rows);
    this.total = this.total + 1;
  }

  private applyUpdate(notification: BellNotificationModel): void {
    if (!notification?.uuid) {
      return;
    }
    const index = this.rows.findIndex(
      (row) => row.notification.uuid === notification.uuid,
    );
    if (index < 0) {
      return;
    }

    const rows = this.rows.slice();
    if (this.matchesFilter(notification)) {
      rows[index] = this.toRow(notification);
    } else {
      rows.splice(index, 1);
      this.total = Math.max(0, this.total - 1);
    }
    this.rows = rows;
  }

  private toRow(notification: BellNotificationModel): BellRow {
    return {
      notification,
      icon: NotificationCenterTools.icon(notification),
      relativeDate: this.relativeDate(notification.created_at),
      fullDate: this.fullDate(notification.created_at),
      canOpen:
        (notification.action_type === 'RECORD' && !!notification.data_model) ||
        (notification.action_type === 'EXTERNAL_URL' &&
          !!notification.action_url),
    };
  }

  private refreshRelativeDates(): void {
    this.rows = this.rows.map((row) => ({
      ...row,
      relativeDate: this.relativeDate(row.notification.created_at),
    }));
  }

  private momentLocale(): string {
    return (this.translate.currentLang || 'fr-CA')
      .toLowerCase()
      .startsWith('fr')
      ? 'fr-ca'
      : 'en';
  }

  private relativeDate(date: string | null): string {
    if (!date) {
      return '';
    }
    const value = moment(date);
    if (!value.isValid()) {
      return '';
    }
    // A clock slightly behind the server must never show « dans quelques secondes ».
    const safe = value.isAfter(moment()) ? moment() : value;
    return safe.locale(this.momentLocale()).fromNow();
  }

  private fullDate(date: string | null): string {
    if (!date) {
      return '';
    }
    const value = moment(date);
    return value.isValid()
      ? value.locale(this.momentLocale()).format('LLL')
      : '';
  }
}
